/**
 * Narrow a role as the database stores it to the app's `WorkspaceRole` union.
 *
 * `workspace_members.role` and `workspace_invitations.role` are `text` with a
 * CHECK constraint, so the generated types can only say `string`. The CHECK is
 * what actually guarantees the value — but it guarantees it in the database, and
 * the cast that used to bridge the gap asserted the guarantee instead of
 * checking it.
 *
 * An unrecognised value resolves to `viewer`, the LEAST privileged role. That
 * direction is deliberate: the old `data.role as WorkspaceRole` would have let
 * any string through, and a string the UI does not recognise must not end up
 * being treated as `admin` by a later `role !== "viewer"` test.
 *
 * (Migration 000010 shipped policies gated on a role `'member'` that the CHECK
 * has never allowed, so "a role name nobody expects" is not hypothetical here.)
 *
 * @module services/workspace/workspaceRole
 */

import type { WorkspaceRole } from '@/types';
import { warn } from '@/services/logger';

const WORKSPACE_ROLES: readonly WorkspaceRole[] = ['admin', 'analyst', 'viewer'];

export const asWorkspaceRole = (value: string): WorkspaceRole => {
  if ((WORKSPACE_ROLES as readonly string[]).includes(value)) {
    return value as WorkspaceRole;
  }
  warn('WorkspaceRole', 'Unrecognised role from the database; treating as viewer', { value });
  return 'viewer';
};
